import { useSyncExternalStore } from 'react';

/**
 * False while React is adopting HTML that the build rendered ahead of time,
 * true from then on and on every page React draws by itself.
 *
 * The landing page arrives as finished HTML so it can paint before any script
 * runs. React then takes that HTML over, and for that to work its first pass
 * must produce exactly the same markup the build did. The build knows nothing
 * about this visitor, so anything that depends on them (storage, the width of
 * the screen, a currency they picked last week) waits for this to turn true and
 * appears one render later.
 *
 * `useSyncExternalStore` is what makes this exact: React uses the server value
 * only while hydrating and the client value everywhere else, so a screen that
 * is not hydrated never sees the false.
 */
const subscribe = () => () => undefined;

export function useHydrated(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => true,
    () => false,
  );
}

declare const __BUILD_TIME__: number | undefined;

/**
 * "Now", as the markup being adopted saw it.
 *
 * While hydrating this is the moment the build rendered the page, so anything
 * worked out from the clock (is the offer still on, what year to print) comes
 * out the same as in the HTML; one render later it is the visitor's own clock.
 * A page served the day after an offer ended therefore still adopts cleanly,
 * and then drops the offer.
 */
export function useRenderNow(): Date {
  const hydrated = useHydrated();
  if (hydrated || typeof __BUILD_TIME__ !== 'number') return new Date();
  return new Date(__BUILD_TIME__);
}
